import {ScreenLayout} from "@/components/layouts/page/ScreenLayout";
import TableLayout from "@/components/layouts/page/TableLayout";

import {useCallback, useEffect, useMemo, useState} from "react";

import {useDeepCompareEffect} from "@hooks/DeepCompareEffect";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {DeleteDataPopup} from "@popups/attentionPopups/DeleteDataPopup";
import {
  getDoctors,
  setInactiveData,
  setPage,
  setSearch,
  setSelectedDoctors,
  setSize,
  setSort,
  setTotalDoctors,
  setTotalPages,
} from "@redux/slices/doctorSlices/tableSlice";
import {
  archiveDoctor,
  deleteDoctor,
  unArchiveDoctor,
} from "@redux/slices/doctorSlices/viewSlice";
import {ControlButton} from "@ui/buttons/ControlButton";
import {TableButton} from "@ui/buttons/TableButton";
import {RootCheckbox} from "@ui/inputs/RootCheckbox";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

export const DoctorTablePage = () => {
  const {openPopup, closePopup} = usePopup();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {demo, role} = useSelector((state) => state.common.candidate);

  const {
    doctors,
    page,
    size,
    sort,
    search,
    totalDoctors,
    serverStatus,
    inactiveData,
    selectedDoctors,
  } = useSelector((state) => state.doctor.table);

  const isAdmin = role === "Admin";

  const [params, setParams] = useState({
    with_archived: inactiveData,
    sort,
  });

  const safePage = useMemo(() => {
    const p = Number(page);
    return Number.isFinite(p) && p > 0 ? p : 1;
  }, [page]);

  const safeSize = useMemo(() => {
    const s = Number(size);
    return Number.isFinite(s) && s > 0 ? s : 5;
  }, [size]);

  const effectiveTotalDoctors = useMemo(() => {
    if (Array.isArray(doctors) && doctors.length > 0) {
      return doctors.length;
    }
    return totalDoctors ?? 0;
  }, [doctors, totalDoctors]);

  const effectiveTotalPages = useMemo(() => {
    return Math.max(1, Math.ceil(effectiveTotalDoctors / safeSize));
  }, [effectiveTotalDoctors, safeSize]);

  const paginatedDoctors = useMemo(() => {
    if (!Array.isArray(doctors) || doctors.length === 0) {
      return [];
    }
    const offset = (safePage - 1) * safeSize;
    return doctors.slice(offset, offset + safeSize);
  }, [doctors, safePage, safeSize]);

  const goToPage = (pageIndex) => {
    dispatch(setPage(pageIndex + 1));
  };

  const handleSelectSize = (option) => {
    dispatch(setSize(+option));
    dispatch(setPage(1));
    if (effectiveTotalDoctors > 0 && +option > 0) {
      dispatch(setTotalPages(Math.ceil(effectiveTotalDoctors / +option)));
    }
  };

  const handleSelectSort = (option) => {
    dispatch(setSort(option));
  };

  const handleSearch = useCallback(
    (value) => {
      clearTimeout(window.searchTimeout);
      window.searchTimeout = setTimeout(() => {
        dispatch(setSearch(value?.toLowerCase?.() ?? ""));
        dispatch(
          getDoctors({
            search: value,
            sort,
            with_archived: inactiveData,
          }),
        );
      }, 500);
    },
    [dispatch, sort, inactiveData],
  );

  const handleCheckFilter = () => {
    dispatch(setPage(1));
    dispatch(setInactiveData(!inactiveData));
  };

  const handleClickArchiveAll = useCallback(async () => {
    try {
      const results = await Promise.all(
        (selectedDoctors ?? []).map(
          (doctor) =>
            !doctor.archived_at && dispatch(archiveDoctor({id: doctor.id})),
        ),
      );

      const successfulResults = results.filter((result) =>
        archiveDoctor.fulfilled.match(result),
      );

      if (
        successfulResults.length > 0 &&
        successfulResults.every((result) =>
          archiveDoctor.fulfilled.match(result),
        )
      ) {
        const newParams = {
          with_archived: inactiveData,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        if (!inactiveData && totalDoctors != null) {
          dispatch(setTotalDoctors(totalDoctors - successfulResults.length));
        }

        setParams(newParams);
        dispatch(getDoctors(newParams));
      }
    } catch (e) {
      console.log("Error archiving all doctors: ", e);
    }
  }, [
    page,
    size,
    search,
    sort,
    inactiveData,
    selectedDoctors,
    totalDoctors,
    dispatch,
  ]);

  const handleClickUnarchiveAll = useCallback(async () => {
    try {
      const results = await Promise.all(
        (selectedDoctors ?? []).map(
          (doctor) =>
            doctor.archived_at && dispatch(unArchiveDoctor({id: doctor.id})),
        ),
      );

      const successfulResults = results.filter((result) =>
        unArchiveDoctor.fulfilled.match(result),
      );

      if (
        successfulResults.length > 0 &&
        successfulResults.every((result) =>
          unArchiveDoctor.fulfilled.match(result),
        )
      ) {
        const newParams = {
          with_archived: inactiveData,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        setParams(newParams);
        dispatch(getDoctors(newParams));
      }
    } catch (e) {
      console.log("Error unarchiving all doctors: ", e);
    }
  }, [page, size, search, sort, inactiveData, selectedDoctors, dispatch]);

  const handleClickArchiveDoctor = useCallback(
    async (item) => {
      try {
        const resultAction = await dispatch(archiveDoctor({id: item.id}));

        if (archiveDoctor.fulfilled.match(resultAction)) {
          const newParams = {
            with_archived: inactiveData,
            ...(search && {search}),
            ...(Object.keys(sort).length !== 0 && {sort}),
          };

          if (!inactiveData && totalDoctors != null) {
            dispatch(setTotalDoctors(totalDoctors - 1));
          }

          setParams(newParams);
          dispatch(getDoctors(newParams));
        }
      } catch (e) {
        console.log("Error archiving doctor: ", e);
      }
    },
    [dispatch, page, size, search, sort, inactiveData, totalDoctors],
  );

  const handleClickUnarchiveDoctor = useCallback(
    async (item) => {
      try {
        const resultAction = await dispatch(unArchiveDoctor({id: item.id}));

        if (unArchiveDoctor.fulfilled.match(resultAction)) {
          const newParams = {
            with_archived: inactiveData,
            ...(search && {search}),
            ...(Object.keys(sort).length !== 0 && {sort}),
          };

          setParams(newParams);
          dispatch(getDoctors(newParams));
        }
      } catch (e) {
        console.log("Error unarchiving doctor: ", e);
      }
    },
    [dispatch, page, size, search, sort, inactiveData],
  );

  const removeDoctor = useCallback(
    async (id) => {
      try {
        const resultAction = await dispatch(deleteDoctor({id}));

        if (deleteDoctor.fulfilled.match(resultAction)) {
          const newParams = {
            with_archived: inactiveData,
            ...(search && {search}),
            ...(Object.keys(sort).length !== 0 && {sort}),
          };

          if (totalDoctors != null) {
            dispatch(setTotalDoctors(totalDoctors - 1));
          }
          setParams(newParams);
          dispatch(getDoctors(newParams));
          closePopup();
        }
      } catch (e) {
        console.log("Error deleting doctor: ", e);
      }
    },
    [
      dispatch,
      page,
      size,
      inactiveData,
      search,
      sort,
      totalDoctors,
      closePopup,
    ],
  );

  const removeAllDoctors = useCallback(async () => {
    try {
      const results = await Promise.all(
        (selectedDoctors ?? []).map((doctor) =>
          dispatch(deleteDoctor({id: doctor.id})),
        ),
      );

      if (
        results.length > 0 &&
        results.every((result) => deleteDoctor.fulfilled.match(result))
      ) {
        const newParams = {
          with_archived: inactiveData,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        if (totalDoctors != null) {
          dispatch(setTotalDoctors(totalDoctors - results.length));
        }
        setParams(newParams);
        dispatch(getDoctors(newParams));
        closePopup();
      }
    } catch (e) {
      console.log("Error deleting all doctors: ", e);
    }
  }, [
    dispatch,
    selectedDoctors,
    page,
    size,
    inactiveData,
    search,
    sort,
    totalDoctors,
    closePopup,
  ]);

  const handleClickDeleteDoctor = (item) => {
    openPopup(
      <DeleteDataPopup
        onClickYes={() => removeDoctor(item.id)}
        onClickNo={() => closePopup()}
      />,
      {closeButton: false},
    );
  };

  const handleClickDeleteAll = () => {
    openPopup(
      <DeleteDataPopup
        onClickNo={() => closePopup()}
        onClickYes={() => removeAllDoctors()}
      />,
      {closeButton: false},
    );
  };

  const controls = [
    {
      id: 0,
      button: (
        <ControlButton
          color={"var(--green)"}
          txt={t("button.add")}
          link
        />
      ),
    },
    ...(isAdmin && !demo
      ? [
          {
            id: 1,
            button: (
              <ControlButton
                color={"var(--red)"}
                txt={t("button.deactivate")}
                onClick={() => handleClickArchiveAll()}
                disabled={
                  !selectedDoctors ||
                  selectedDoctors.length <= 1 ||
                  !selectedDoctors.some((d) => !d.archived_at)
                }
              />
            ),
          },
        ]
      : []),
  ];

  const advancedControls = [
    {
      id: 3,
      button: (
        <ControlButton
          color={"var(--green)"}
          txt={t("table.button.activate")}
          onClick={() => handleClickUnarchiveAll()}
          disabled={
            !selectedDoctors ||
            selectedDoctors.length <= 1 ||
            !selectedDoctors.some((d) => d.archived_at) ||
            selectedDoctors.some((d) => !d.archived_at)
          }
        />
      ),
    },
    {
      id: 4,
      button: (
        <ControlButton
          color={"var(--red)"}
          txt={t("table.button.delete")}
          onClick={() => handleClickDeleteAll()}
          disabled={
            !selectedDoctors ||
            selectedDoctors.length <= 1 ||
            !selectedDoctors.some((d) => d.archived_at) ||
            selectedDoctors.some((d) => !d.archived_at)
          }
        />
      ),
    },
  ];

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: t("table.profile_name"),
        accessor: "name",
        sortable: true,
        width: 600,
      },
      {
        Header: t("table.status"),
        accessor: "state",
        sortable: true,
        Cell: ({value}) => (value ? t(`table.status_${value}`) : ""),
        width: isAdmin ? 300 : 350,
      },
      {
        Header: t("table.created"),
        accessor: "created_at",
        sortable: true,
        Cell: ({value}) => (value ? moment(value).format("DD-MM-YYYY") : ""),
        width: 305,
      },
      {
        Header: t("table.actions"),
        Cell: ({row}) => (
          <div className={"flex justify-end gap-[8rem] phone:gap-[40rem]"}>
            <TableButton
              icon={"view"}
              color={"var(--orange)"}
              label={t("table.button.view")}
              action={"view"}
              item={row.original}
            />

            {isAdmin && !demo && (
              <>
                {row.original.archived_at ? (
                  <>
                    <TableButton
                      icon={"activate"}
                      color={"var(--green)"}
                      label={t("table.button.activate")}
                      action={"activate"}
                      onClickAction={() =>
                        handleClickUnarchiveDoctor(row.original)
                      }
                      item={row.original}
                    />
                    <TableButton
                      icon={"delete"}
                      color={"var(--red)"}
                      label={t("table.button.delete")}
                      action={"delete"}
                      onClickAction={() =>
                        handleClickDeleteDoctor(row.original)
                      }
                      item={row.original}
                    />
                  </>
                ) : (
                  <TableButton
                    icon={"deactivate"}
                    color={"var(--red)"}
                    label={t("table.button.deactivate")}
                    action={"deactivate"}
                    onClickAction={() => handleClickArchiveDoctor(row.original)}
                    item={row.original}
                  />
                )}
              </>
            )}
          </div>
        ),
        width: 210,
      },
    ];

    if (isAdmin && !demo) {
      baseColumns.unshift({
        id: "selection",
        Header: ({getToggleAllRowsSelectedProps}) => (
          <div>
            <RootCheckbox {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({row}) => (
          <div>
            <RootCheckbox {...row.getToggleRowSelectedProps()} />
          </div>
        ),
        width: 50,
      });
    }

    return baseColumns;
  }, [
    t,
    demo,
    isAdmin,
    handleClickArchiveDoctor,
    handleClickUnarchiveDoctor,
    handleClickDeleteDoctor,
  ]);

  useEffect(() => {
    setParams((prev) => {
      const next = {
        ...prev,
        with_archived: inactiveData,
      };
      if (search) {
        next.search = search;
      } else {
        delete next.search;
      }
      if (Object.keys(sort).length !== 0) {
        next.sort = sort;
      }
      return next;
    });
  }, [search, sort, inactiveData]);

  useDeepCompareEffect(() => {
    dispatch(getDoctors(params));
  }, [params]);

  useEffect(() => {
    if (safeSize > 0) {
      dispatch(setTotalPages(effectiveTotalPages));
    }
  }, [effectiveTotalPages, safeSize, dispatch]);

  useEffect(() => {
    if (safePage > effectiveTotalPages && effectiveTotalDoctors > 0) {
      dispatch(setPage(effectiveTotalPages));
    }
  }, [effectiveTotalDoctors, effectiveTotalPages, safePage, dispatch]);

  return (
    <ScreenLayout
      title={t("page.avatars.title")}
      controlsList={
        !demo && isAdmin
          ? [...controls, ...(isAdmin && inactiveData ? advancedControls : [])]
          : []
      }
    >
      <TableLayout
        serverStatus={serverStatus}
        columns={columns}
        data={paginatedDoctors}
        dataSort={sort}
        selectSort={handleSelectSort}
        dataSize={safeSize}
        selectSize={handleSelectSize}
        searchPlaceholder={t("table.search_avatars")}
        searchValue={search}
        onSearch={handleSearch}
        totalPages={effectiveTotalPages}
        currentPage={safePage}
        toPage={goToPage}
        setSelected={
          isAdmin && !demo
            ? (items) => dispatch(setSelectedDoctors(items))
            : undefined
        }
        filterCheckbox={
          !demo ? (
            <RootCheckbox
              onChange={() => handleCheckFilter()}
              label={t("page.avatars.checkbox_filter")}
              checked={inactiveData}
            />
          ) : undefined
        }
      />
    </ScreenLayout>
  );
};
